import React from "react";

import { Flex, type ImageProps, Link, type LinkProps } from "@chakra-ui/react";
import { LogoBrandmark, LogoInline, LogoStacked } from "../../theme/logos";

export interface LogoVariant {
  imageProps: Partial<ImageProps>;
  linkProps?: Partial<LinkProps>;
}

export interface LogoProps extends Omit<ImageProps, "src"> {
  variant: "brandmark" | "inline" | "stacked" | "square";
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  alt?: string;
  href?: string;
  backgroundColor?: string;
  color?: string | { _light: string; _dark: string };
  hasBreakpointSizes?: boolean;
  isSquare?: boolean;
  isDarkMint?: boolean;
  linkProps?: Omit<LinkProps, "href">;
  src?: string;
}

const LOGO_SIZES = {
  xs: { base: 4, md: 6, lg: 8 },
  sm: { base: 6, md: 8, lg: 12 },
  md: { base: 8, md: 12, lg: 16 },
  lg: { base: 12, md: 16, lg: 20 },
  xl: { base: 16, md: 20, lg: 24 }
};

export const Logo: React.FC<LogoProps> = ({
  variant = "inline",
  size = "md",
  alt = "Euka Logo",
  href,
  backgroundColor,
  color,
  hasBreakpointSizes = true,
  isSquare = false,
  isDarkMint = true,
  src: _src,
  linkProps
}) => {
  const logoColor = isDarkMint
    ? { _light: color, _dark: "success.500" }
    : color;
  const logoSize = hasBreakpointSizes ? LOGO_SIZES[size] : LOGO_SIZES[size].md;
  const isLogoSquare = isSquare || variant === "square";
  const logoSquarePadding = hasBreakpointSizes
    ? {
        base: LOGO_SIZES[size].base / 8,
        md: LOGO_SIZES[size].md / 8,
        lg: LOGO_SIZES[size].lg / 8
      }
    : LOGO_SIZES[size].md / 8;

  const getLogoVariant = () => {
    if (variant === "stacked") {
      return (
        <LogoStacked
          color={logoColor}
          aria-label={alt}
          w={logoSize}
          data-testid="logo-stacked"
        />
      );
    } else if (variant === "inline") {
      return (
        <LogoInline
          color={logoColor}
          aria-label={alt}
          w={logoSize}
          data-testid="logo-inline"
        />
      );
    } else {
      return (
        <LogoBrandmark
          color={logoColor}
          aria-label={alt}
          w={logoSize}
          data-testid="logo-brandmark"
        />
      );
    }
  };

  return (
    <Flex
      data-bark-component="Logo"
      bgColor={backgroundColor}
      aspectRatio={isLogoSquare ? "1/1" : "auto"}
      p={isLogoSquare ? logoSquarePadding : "0"}
      w="fit-content"
      h="fit-content"
      data-testid="logo-container"
    >
      {href ? (
        <Link href={href} {...linkProps} data-testid="logo-link-wrapper">
          {getLogoVariant()}
        </Link>
      ) : (
        getLogoVariant()
      )}
    </Flex>
  );
};

export default Logo;
